import { useCallback, useMemo } from 'react';
import type { FetchPolicy } from '@apollo/client';
import { useCursorPagination } from '@frameio/graphql-client';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useGetWorkspacesForNavigationQuery } from '../gql/generated/GetWorkspacesForNavigation';

const PAGE_SIZE = 100;

export const usePaginatedWorkspaces = ({
  fetchPolicy,
}: {
  fetchPolicy?: FetchPolicy;
} = {}) => {
  const accountId = useActiveAccountId();
  const { data, loading, fetchNext } = useCursorPagination(
    useGetWorkspacesForNavigationQuery({
      variables: {
        accountId,
        page: { first: PAGE_SIZE },
        filter: null,
        order: { field: 'NAME', direction: 'ASC_NULLS_LAST' },
      },
      skip: !accountId,
      fetchPolicy,
    })
  );

  const fetchNextPage = useCallback(() => {
    const pageInfo = data?.account?.workspaces.pageInfo || {
      hasNextPage: false,
    };
    if (!loading && pageInfo.hasNextPage) {
      fetchNext({
        count: PAGE_SIZE,
        pageInfo: {
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor,
        },
      });
    }
  }, [data, loading, fetchNext]);

  const items = useMemo(() => {
    return (
      data?.account?.workspaces.nodes.map((node) => ({
        id: node.id,
      })) ?? []
    );
  }, [data]);

  const hasSingleWorkspace = items.length === 1;

  return {
    fetchNext: fetchNextPage,
    hasSingleWorkspace,
    isLimitedToOneWorkspace:
      data?.account?.subscription?.limitedResources?.workspaces?.limit53 === 1,
    items,
    loading,
    singleWorkspace: hasSingleWorkspace ? items[0] : undefined,
  };
};
